import Collection from './collection.js';
import LeaveRequest from 'newModels/leave-request.js';
import LeaveApprovals from 'newCollections/leave-approvals.js';
import axios from 'axios';

export default class LeaveRequests extends Collection {
    url() {
        if (this.employee) {
            return `${this.employee.url()}/leaveRequests`;
        }

        if (this.company) {
            return `${this.company.url()}/leaveRequests`;
        }

        return `/LeaveRequests`;
    }

    initialize(models, {company = null, employee = null}) {
        super.initialize(...arguments);
        this.model = LeaveRequest;
        this.company = company;
        this.employee = employee;
    }

    comparator(a, b) {
        const typeA = a.getType();
        const typeB = b.getType();

        if (!typeA || !typeB) {
            return a.id > b.id ? 1 : -1;
        }

        if (typeA.get('annual')) {
            return -1;
        }

        if (typeB.get('annual')) {
            return 1;
        }

        if (!typeA.get('company')) {
            return -1;
        }

        if (!typeA.get('optional')) {
            return -1;
        }

        return (typeA.get('name') > typeB.get('name')) ? 1 : -1;
    }

    asCalendarEvents() {
        return this.map(model => model.asCalendarEvent());
    }

    getRequestedDays() {
        return this.reduce((memo, model) => {
            return memo + model.getRequestedDays();
        }, 0);
    }

    static fetchForBalances({
        employee = null,
        typeIds = [],
        start = new Date(),
        end = new Date()
    }) {
        return new LeaveRequests(null, {employee, pagination: false})
            .fetch({filter: {where: {
                typeId: {inq: typeIds},
                status: 2,
                or: [
                    {
                        dateTo: {gte: start},
                        dateFrom: {lte: end}
                    },
                    {
                        and: [
                            {dateFrom: {gte: start}},
                            {dateFrom: {lte: end}}
                        ],
                        dateTo: null
                    }
                ]
            }}});
    }

    static fetchForTypes({
        typeIds = null,
        employeeIds = null
    }) {
        const url = new LeaveRequests(null, {company: app.company}).finalUrl();
        return axios.post(
            `${url}`,
            {filter: {
                where: {
                    employeeId: {inq: employeeIds},
                    typeId: {inq: typeIds},
                    status: 2
                },
                limit: 9999
            }},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(r => new LeaveRequests(r.data, {company: app.company}))
        .catch(err => {
            throw err.response.data.error;
        });
    }

    static fetchForCalendar({
        employees = null,
        start = new Date(),
        end = new Date(),
        status = null,
        location = null
    }) {
        let filterJSON = {
            filter: {
                where: {
                    or: [
                        {
                            dateFrom: {gte: start},
                            dateTo: {lte: end}
                        },
                        {
                            dateTo: {gte: start},
                            dateFrom: {lte: end}
                        },
                        {
                            and: [
                                {dateFrom: {gte: start}},
                                {dateFrom: {lte: end}}
                            ],
                            dateTo: null
                        }
                    ]
                },
                limit: 9999
            }
        };
        if (status) {
            filterJSON.filter.where.status = status;
        }

        if (employees.length === 1) {
            // if it's a single employee, fetch leave requests through
            // his/her relation
            const employee = employees.at(0);
            return new LeaveRequests(null, {employee, pagination: false})
                .fetch(filterJSON)
                .then(leaves => {
                    // if it's the user employee's own leaves
                    if (!app.employee || employee.id === app.employee.id) {
                        leaves.each(leave => {leave.employee = employee;});
                        return leaves;
                    }
                    const approvals = new LeaveApprovals(null, {
                        approver: app.employee
                    });
                    return approvals.fetch({
                        filter: {where: {requesterId: employee.id}}
                    }).then(() => {
                        const layer = approvals.length ?
                            approvals.at(0).get('layer') : -1;
                        const filteredLeaves = new LeaveRequests(
                            leaves.filter(model => {
                                if (!model.isAwaiting()) {
                                    return true;
                                }
                                let approvalStatus =
                                    model.get('approvalStatus') || 0;
                                return (layer <= approvalStatus + 1);
                            }), {employee}
                        );
                        filteredLeaves.each(leave => {
                            leave.employee = employee;
                        });
                        return filteredLeaves;
                    });
                });
        } else {
            // if it's multiple employees, fetch leave requests through the
            // company relation. We assume app.company because the calendar
            // is not built to load data from multiple companies
            filterJSON.filter.where.employeeId = {
                inq: employees.map(employee => employee.id)
            };
            const url = new LeaveRequests(
                null, {company: app.company}
            ).finalUrl();
            return axios.post(
                `${url}`,
                filterJSON,
                {headers: {'Authorization': app.user.getAccessToken().id}}
            ).then(r => {
                let leaves = new LeaveRequests(
                    r.data, {company: app.company}
                );
                leaves.each(leave => {
                    leave.employee = employees.find(employee =>
                        leave.get('employeeId') === employee.id
                    );
                });

                if (location && leaves.length > 0) {
                    const leaveByLocation = leaves.filter(leave =>
                        leave.employee.get('companyLocationId') === location
                    );

                    return new LeaveRequests(
                        leaveByLocation, {company: app.company}
                    );
                }

                return leaves;
            }).catch(err => {
                throw err.response.data.error;
            });
        }
    }
}



// WEBPACK FOOTER //
// ./app/new-collections/leave-requests.js